import React from "react";

import type { FinCostCenter, FinCostCenterGroup } from "../api/finTypes";
import { formatCurrency } from "../../dp/utils/format";
import { parseMoneyInput } from "../utils/fin";
import CostCenterSelect from "./CostCenterSelect";

export type AllocationRow = { costCenterId: string; amount: string };

type Props = {
  rows: AllocationRow[];
  onChange: (rows: AllocationRow[]) => void;
  /** Valor do título, para conferir a soma (NaN enquanto o valor não foi digitado). */
  total: number;
  costCenters: FinCostCenter[];
  groups: FinCostCenterGroup[];
  disabled?: boolean;
};

const toInput = (n: number) => n.toFixed(2).replace(".", ",");

/** Soma das fatias digitadas, em centavos inteiros. */
export const allocationCents = (rows: AllocationRow[]) =>
  rows.reduce((acc, r) => acc + Math.round((parseMoneyInput(r.amount) || 0) * 100), 0);

/** Erros do rateio para o formulário: centros, valores e soma. */
export function allocationErrors(rows: AllocationRow[], total: number): string[] {
  const list: string[] = [];
  if (rows.length < 2) list.push("O rateio precisa de pelo menos dois centros de custo.");
  if (rows.some((r) => !r.costCenterId)) list.push("Escolha o centro de custo de cada fatia do rateio.");
  const ids = rows.map((r) => r.costCenterId).filter(Boolean);
  if (new Set(ids).size !== ids.length) list.push("O rateio repete um centro de custo.");
  if (rows.some((r) => !(parseMoneyInput(r.amount) > 0))) list.push("Informe o valor de cada fatia do rateio.");
  if (Number.isFinite(total) && allocationCents(rows) !== Math.round(total * 100)) {
    list.push("A soma do rateio precisa ser igual ao valor do lançamento.");
  }
  return list;
}

/** Payload do rateio para a API. */
export const allocationPayload = (rows: AllocationRow[]) =>
  rows.map((r) => ({ costCenterId: r.costCenterId, amount: parseMoneyInput(r.amount) }));

/**
 * Rateio de um lançamento entre centros de custo (DRE, 17/10/2026): uma linha por
 * centro com o valor em reais, a soma conferida com o valor do lançamento e o
 * botão de dividir igualmente (o centavo de sobra vai para a última fatia).
 */
export default function AllocationEditor({ rows, onChange, total, costCenters, groups, disabled }: Props) {
  const set = (i: number, patch: Partial<AllocationRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, { costCenterId: "", amount: "" }]);

  const splitEvenly = () => {
    if (!Number.isFinite(total) || total <= 0 || !rows.length) return;
    const cents = Math.round(total * 100);
    const base = Math.floor(cents / rows.length);
    onChange(rows.map((r, i) => ({ ...r, amount: toInput((i === rows.length - 1 ? cents - base * (rows.length - 1) : base) / 100) })));
  };

  const sum = allocationCents(rows) / 100;
  const diff = Number.isFinite(total) ? Math.round((total - sum) * 100) / 100 : NaN;

  return (
    <div className="finAlloc">
      {rows.map((row, i) => (
        <div className="finAlloc__row" key={i}>
          <CostCenterSelect
            id={`alloc-cc-${i}`}
            value={row.costCenterId}
            onChange={(v) => set(i, { costCenterId: v })}
            costCenters={costCenters}
            groups={groups}
            disabled={disabled}
          />
          <input
            className="dpInput finAlloc__amount"
            inputMode="decimal"
            placeholder="0,00"
            aria-label={`Valor da fatia ${i + 1}`}
            value={row.amount}
            onChange={(e) => set(i, { amount: e.target.value })}
            disabled={disabled}
          />
          <button
            type="button"
            className="finGhostBtn finGhostBtn--danger finAlloc__remove"
            onClick={() => remove(i)}
            disabled={disabled || rows.length <= 2}
            aria-label={`Remover a fatia ${i + 1}`}
          >
            Remover
          </button>
        </div>
      ))}
      <div className="finAlloc__footer">
        <div className="finAlloc__buttons">
          <button type="button" className="ecoBtn ecoBtn--sm" onClick={add} disabled={disabled}>
            Adicionar centro
          </button>
          <button type="button" className="ecoBtn ecoBtn--sm" onClick={splitEvenly} disabled={disabled || !(total > 0)}>
            Dividir igualmente
          </button>
        </div>
        <span className={`finAlloc__sum ${Number.isFinite(diff) && diff !== 0 ? "is-off" : ""}`}>
          Soma {formatCurrency(sum)}
          {Number.isFinite(diff) && diff !== 0 ? ` · ${diff > 0 ? "faltam" : "sobram"} ${formatCurrency(Math.abs(diff))}` : ""}
        </span>
      </div>
    </div>
  );
}
